import {KnowledgeBaseRepository} from "@buildwithflux/repositories";
import {Logger} from "@buildwithflux/shared";
import {KnowledgeBaseContext} from "@buildwithflux/models";

import {CurrentUserService} from "modules/auth";
import {
    CopilotChatStoreGetter,
    CopilotChatStoreSetter,
} from "components/pages/document/components/editor/components/right_drawer/copilot_chat/state/useCopilotChatStore";

/**
 * Fetches knowledge base suggestions for the current user
 */
export function fetchKnowledgeBaseSuggestions(
    set: CopilotChatStoreSetter,
    get: CopilotChatStoreGetter,
    currentUserService: CurrentUserService,
    knowledgeBaseRepository: KnowledgeBaseRepository,
    logger: Logger,
) {
    return async () => {
        const currentUser = currentUserService.getCurrentUser();
        if (!currentUser) return;

        set((draft) => {
            draft.knowledgeBaseSuggestionsLoading = true;
        });

        try {
            const suggestions = await knowledgeBaseRepository.getKnowledgeSuggestions(currentUser.uid);

            const {currentDocumentUid} = get();

            if (!currentDocumentUid) {
                return;
            }

            const filteredSuggestions = suggestions.filter((s) => s.project_uid === currentDocumentUid);

            set((draft) => {
                draft.knowledgeBaseSuggestions = filteredSuggestions;
                draft.knowledgeBaseSuggestionsLoading = false;
            });
        } catch (error) {
            logger.error("Failed to fetch knowledge base suggestions:", error);
            set((draft) => {
                draft.knowledgeBaseSuggestionsLoading = false;
            });
        }
    };
}

/**
 * Approves a knowledge base suggestion
 */
export function approveKnowledgeBaseSuggestion(
    set: CopilotChatStoreSetter,
    get: CopilotChatStoreGetter,
    currentUserService: CurrentUserService,
    knowledgeBaseRepository: KnowledgeBaseRepository,
    logger: Logger,
) {
    return async (suggestionUid: string) => {
        const currentUser = currentUserService.getCurrentUser();
        if (!currentUser) return;

        try {
            await knowledgeBaseRepository.approveKnowledgeSuggestion(suggestionUid, currentUser.uid);

            void get().fetchKnowledgeBaseSuggestions();
        } catch (error) {
            logger.error("Failed to approve knowledge base suggestion:", error);
        }
    };
}

/**
 * Declines a knowledge base suggestion
 */
export function declineKnowledgeBaseSuggestion(
    set: CopilotChatStoreSetter,
    get: CopilotChatStoreGetter,
    knowledgeBaseRepository: KnowledgeBaseRepository,
    logger: Logger,
) {
    return async (suggestionUid: string) => {
        try {
            await knowledgeBaseRepository.declineKnowledgeSuggestion(suggestionUid);

            void get().fetchKnowledgeBaseSuggestions();
        } catch (error) {
            logger.error("Failed to decline knowledge base suggestion:", error);
        }
    };
}

/**
 * Updates a knowledge base suggestion
 */
export function updateKnowledgeBaseSuggestion(
    set: CopilotChatStoreSetter,
    get: CopilotChatStoreGetter,
    knowledgeBaseRepository: KnowledgeBaseRepository,
    logger: Logger,
) {
    return async (
        suggestionUid: string,
        knowledge: string,
        useWhen?: string,
        context?: KnowledgeBaseContext,
        wasEdited?: boolean,
    ) => {
        try {
            await knowledgeBaseRepository.updateKnowledgeSuggestion(
                suggestionUid,
                knowledge,
                useWhen,
                context,
                wasEdited,
            );

            void get().fetchKnowledgeBaseSuggestions();
        } catch (error) {
            logger.error("Failed to update knowledge base suggestion:", error);
        }
    };
}

/**
 * Subscribe to knowledge base suggestions for the current user
 */
export function subscribeToKnowledgeBaseSuggestions(
    set: CopilotChatStoreSetter,
    get: CopilotChatStoreGetter,
    currentUserService: CurrentUserService,
    knowledgeBaseRepository: KnowledgeBaseRepository,
    logger: Logger,
) {
    return () => {
        const currentUser = currentUserService.getCurrentUser();
        if (!currentUser) return;

        return knowledgeBaseRepository.subscribeToKnowledgeBaseSuggestions(
            currentUser.uid,
            (addedOrChangedSuggestions) => {
                const {currentDocumentUid} = get();

                if (!currentDocumentUid) {
                    return;
                }

                const filteredSuggestions = addedOrChangedSuggestions.filter(
                    (s) => s.project_uid === currentDocumentUid,
                );

                set((draft) => {
                    // eslint-disable-next-line @buildwithflux/no-array-foreach
                    filteredSuggestions.forEach((suggestion) => {
                        draft.knowledgeBaseSuggestions = draft.knowledgeBaseSuggestions.filter(
                            (s) => s.uid !== suggestion.uid,
                        );

                        draft.knowledgeBaseSuggestions.push(suggestion);
                    });
                    draft.knowledgeBaseSuggestionsLoading = false;
                });
            },
            (removedSuggestions) => {
                set((draft) => {
                    // eslint-disable-next-line @buildwithflux/no-array-foreach
                    removedSuggestions.forEach((suggestion) => {
                        draft.knowledgeBaseSuggestions = draft.knowledgeBaseSuggestions.filter(
                            (s) => s.uid !== suggestion.uid,
                        );
                    });
                });
            },
            (error) => {
                logger.error("Error subscribing to knowledge base suggestions", error);
                set((draft) => {
                    draft.knowledgeBaseSuggestionsLoading = false;
                });
            },
        );
    };
}
